import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  PopoverProps,
} from "@chakra-ui/react";
import React, { useRef } from "react";
import { CSSObjectWithLabel, GroupBase, SelectInstance } from "react-select";
import { StateManagerProps } from "react-select/dist/declarations/src/useStateManager";
import { defaultComponents } from "utils/useSelectStyles";

const selectComponents = { ...defaultComponents, DropdownIndicator: null };

const selectStyles = {
  control: () => ({
    marginTop: 8,
    marginBottom: 8,
    marginLeft: ".5rem",
    marginRight: ".5rem",
  }),
  menu: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
    ...provided,
    position: "static",
    marginTop: 0,
    marginBottom: 0,
    boxShadow: "none",
    borderTopWidth: 1,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  }),
};

type RenderSelectProps<
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Pick<
  StateManagerProps<Option, IsMulti, Group>,
  | "onMenuClose"
  | "autoFocus"
  | "menuIsOpen"
  | "controlShouldRenderValue"
  | "components"
  | "styles"
> & {
  ref: React.RefObject<SelectInstance<Option, IsMulti, Group>>;
};

type RenderSelectContext = { onClose: () => void };

type Props<
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = {
  children: React.ReactElement;
  popoverProps?: Omit<PopoverProps, "initialFocusRef" | "isLazy" | "children">;
  renderSelect: (
    selectProps: RenderSelectProps<Option, IsMulti, Group>,
    context: RenderSelectContext,
  ) => React.ReactElement;
};

export default function ButtonPopoverSelect<
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({ children, popoverProps, renderSelect }: Props<Option, IsMulti, Group>) {
  const selectRef = useRef<SelectInstance<Option, IsMulti, Group>>(null);

  return (
    <Popover initialFocusRef={selectRef} isLazy {...popoverProps}>
      {({ onClose }) => (
        <>
          <PopoverTrigger>{children}</PopoverTrigger>
          <PopoverContent>
            <PopoverArrow />
            <PopoverBody p={0}>
              {renderSelect(
                {
                  ref: selectRef,
                  onMenuClose: onClose,
                  autoFocus: true,
                  menuIsOpen: true,
                  controlShouldRenderValue: false,
                  components: selectComponents,
                  styles: selectStyles,
                },
                { onClose },
              )}
            </PopoverBody>
          </PopoverContent>
        </>
      )}
    </Popover>
  );
}
